/**
 * BillPaymentPage — citizen-side electricity / water / cable / internet
 * bill payment.
 *
 * Live (service-financial-services, citizen endpoints):
 *   - GET  /v1/bills/providers   — active providers
 *   - POST /v1/bills/lookup      — synthetic biller lookup (test-mode)
 *   - POST /v1/bills/pay         — debit wallet + persist bill_payment
 *
 * RBAC: Bills/read-providers + Bills/pay-own — both granted to the
 * `customer` role by 20260509_nipost_pay_destub_perms.sql.
 */
import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Droplets, FileText, Loader2, Search, Tv, Wifi, Zap } from 'lucide-react';

import {
  listCitizenBillProviders,
  lookupBill,
  payCitizenBill,
  type BillLookupResponse,
  type BillPaymentReceipt,
  type CitizenBillProvider,
  type ListCitizenBillProvidersResponse,
} from '../../lib/api/bills';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  customerWalletQueryKey,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

interface CategoryTab {
  readonly id: string;
  readonly label: string;
  readonly icon: typeof Zap;
  readonly category: string;
  readonly inputLabel: string;
}

const TABS = [
  { id: 'electricity', label: 'Electricity', icon: Zap,      category: 'ELECTRICITY', inputLabel: 'Meter number' },
  { id: 'water',       label: 'Water',       icon: Droplets, category: 'WATER',       inputLabel: 'Account number' },
  { id: 'cable',       label: 'Cable TV',    icon: Tv,       category: 'CABLE',       inputLabel: 'Smart card / IUC number' },
  { id: 'internet',    label: 'Internet',    icon: Wifi,     category: 'INTERNET',    inputLabel: 'Account number' },
] as const satisfies readonly CategoryTab[];

export function BillPaymentPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const wallet = useCustomerWallet(customerId, token);

  const [activeTab, setActiveTab] = useState<string>('electricity');
  const [providerCode, setProviderCode] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [lookupResult, setLookupResult] = useState<BillLookupResponse | null>(null);

  const providers = useQuery<ListCitizenBillProvidersResponse, Error>({
    queryKey: ['citizen-bill-providers'] as const,
    queryFn: () => listCitizenBillProviders({ bearerToken: token ?? undefined }),
    enabled: token !== null,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const tabMeta = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  const filteredProviders = useMemo<readonly CitizenBillProvider[]>(() => {
    if (!providers.data) return [];
    return providers.data.items.filter((p) => p.category.toUpperCase() === tabMeta.category);
  }, [providers.data, tabMeta]);

  const lookup = useMutation<BillLookupResponse, Error, undefined>({
    mutationFn: () =>
      lookupBill(
        { providerCode, accountNumber },
        { bearerToken: token ?? undefined },
      ),
    onSuccess: (data) => {
      setLookupResult(data);
      // Pre-fill the amount field with the synthetic outstanding amount so
      // the citizen can pay-in-full or override.
       
      const rounded = Math.round(parseFloat(data.outstandingAmount));
      setAmount(String(rounded));
    },
  });

  const pay = useMutation<BillPaymentReceipt, Error, undefined>({
    mutationFn: () => {
      if (!wallet.data) throw new Error('Wallet not loaded.');
      return payCitizenBill(
        {
          providerCode,
          accountNumber,
          amount: amount.length > 0 ? amount : '0',
          currency: wallet.data.currency,
          idempotencyKey: `bill-${Date.now()}`,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) }),
  });

  function handleLookup(): void {
    if (providerCode.length === 0 || accountNumber.length === 0) return;
    setLookupResult(null);
    lookup.mutate(undefined);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // eslint-disable-next-line upmp/no-number-money -- amount-input guard
    if (providerCode.length === 0 || accountNumber.length === 0 || Number(amount) <= 0) return;
    pay.mutate(undefined);
  }

  return (
    <div>
      <PageHeader title="Bill payment" description="Pay electricity, water, cable, and internet bills from your wallet." />

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        noValidate
      >
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-6 space-y-5">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(t.id);
                    setProviderCode('');
                    setLookupResult(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === t.id ? 'bg-white text-nipost-green shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>

          <div>
            <label htmlFor="provider" className="block text-sm text-muted mb-1">
              Provider
            </label>
            {providers.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading providers…
              </p>
            ) : providers.isError ? (
              <p role="alert" className="text-sm text-red-600">
                Could not load providers — please try again.
              </p>
            ) : (
              <select
                id="provider"
                value={providerCode}
                onChange={(e) => {
                  setProviderCode(e.target.value);
                  setLookupResult(null);
                }}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              >
                <option value="">Select provider</option>
                {filteredProviders.map((p) => (
                  <option key={p.id} value={p.code}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label htmlFor="account" className="block text-sm text-muted mb-1">
              {tabMeta.inputLabel}
            </label>
            <div className="flex gap-2">
              <input
                id="account"
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  setLookupResult(null);
                }}
                placeholder={tabMeta.inputLabel}
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
              <button
                type="button"
                onClick={handleLookup}
                disabled={providerCode.length === 0 || accountNumber.length === 0 || lookup.isPending}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
              >
                {lookup.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Lookup
              </button>
            </div>
            {lookupResult ? (
              <div
                className="mt-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800"
                data-testid="bill-lookup-result"
              >
                <div className="font-medium">{lookupResult.customerName}</div>
                <div>Outstanding: {formatNaira(lookupResult.outstandingAmount)}</div>
              </div>
            ) : null}
            {lookup.isError ? (
              <p className="mt-1 text-xs text-red-600">{lookup.error.message}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="bill-amount" className="block text-sm text-muted mb-1">
              Amount (₦)
            </label>
            <input
              id="bill-amount"
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 flex justify-between text-sm">
            <span className="text-muted">Wallet balance</span>
            <span className="font-semibold">
              {wallet.data
                ? formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')
                : '—'}
            </span>
          </div>

          {pay.isError ? (
            <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {pay.error.message}
            </div>
          ) : null}

          {pay.isSuccess ? (
            <div
              className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
              role="status"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Bill paid — receipt{' '}
              <span className="font-mono">{pay.data.providerRef}</span>. Wallet balance{' '}
              {formatNaira(pay.data.walletBalance)}.
            </div>
          ) : null}

          <button
            type="submit"
            disabled={
              providerCode.length === 0 ||
              accountNumber.length === 0 ||
              // eslint-disable-next-line upmp/no-number-money -- disabled-state guard
              !amount || Number(amount) <= 0 ||
              pay.isPending ||
              !wallet.data
            }
            className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {pay.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Pay{amount ? ` — ${formatNaira(amount)}` : ''}
          </button>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">Quick tips</h3>
            <ul className="space-y-3 text-sm text-muted">
              <li className="flex gap-2">
                <Zap className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                Prepaid meters credit instantly upon payment.
              </li>
              <li className="flex gap-2">
                <Tv className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                Cable TV renewals activate within 5 minutes.
              </li>
              <li className="flex gap-2">
                <FileText className="w-4 h-4 text-nipost-green flex-shrink-0 mt-0.5" />
                Save the confirmation token as proof of payment.
              </li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  );
}
